import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  masterweeks: PropTypes.array,
  companies: PropTypes.array,
  services: PropTypes.array,
  patientNames: PropTypes.array,
};

const defaultProps = {
  title: '',
  masterweeks: [],
};

@autobind
class TableMasterweeks extends BaseComponent {
  constructor(props) {
    super(props);
  }

  createColumns() {
    return [{
      title: 'NO.',
      dataIndex: 'number',
      sorter: (a, b) => a.number - b.number,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Company',
      dataIndex: 'company',
      sorter: (a, b) => a.company?.localeCompare(b.company),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Active Period',
      dataIndex: 'activeString',
      sorter: (a, b) => a.startDate - b.startDate,
    }, {
      title: 'Mon Hrs.',
      dataIndex: 'monday',
    }, {
      title: 'Tue Hrs.',
      dataIndex: 'tuesday',
    }, {
      title: 'Wed Hrs.',
      dataIndex: 'wednesday',
    }, {
      title: 'Thu Hrs.',
      dataIndex: 'thursday',
    }, {
      title: 'Fri Hrs.',
      dataIndex: 'friday',
    }, {
      title: 'Sat Hrs.',
      dataIndex: 'saturday',
    }, {
      title: 'Sun Hrs.',
      dataIndex: 'sunday',
    }, {
      title: 'Total Hrs.',
      dataIndex: 'totalHoursString',
    }, {
      title: 'Total Shifts',
      dataIndex: 'totalShifts',
    }];
  }

  createDataSource(masterweeks) {
    const { patientNames } = this.props;

    const dataSource = [];
    masterweeks.forEach((masterweek, index) => {
      const monday = masterweek.getRemoteObject('monday');
      const tuesday = masterweek.getRemoteObject('tuesday');
      const wednesday = masterweek.getRemoteObject('wednesday');
      const thursday = masterweek.getRemoteObject('thursday');
      const friday = masterweek.getRemoteObject('friday');
      const saturday = masterweek.getRemoteObject('saturday');
      const sunday = masterweek.getRemoteObject('sunday');
      const totalHoursInMillis = (monday.getLong('billHours') ?? 0)
        + (tuesday.getLong('billHours') ?? 0)
        + (wednesday.getLong('billHours') ?? 0)
        + (thursday.getLong('billHours') ?? 0)
        + (friday.getLong('billHours') ?? 0)
        + (saturday.getLong('billHours') ?? 0)
        + (sunday.getLong('billHours') ?? 0);

      dataSource.push({
        key: masterweek.getId(),
        id: masterweek.getId(),
        number: index + 1,
        company: this.findCompanyName(masterweek),
        patientId: masterweek.getLong('patientId'),
        patientName: patientNames.find(p => p.id === masterweek.getLong('patientId'))?.name,
        startDate: masterweek.getLong('startDate'),
        startDateUsString: masterweek.getString('startDateUsString'),
        endDate: masterweek.getLong('endDate'),
        endDateUsString: masterweek.getString('endDateUsString'),
        activeString: `${masterweek.getString('startDateUsString')}-${masterweek.getString('endDateUsString')}`,
        monday: this.createMasterweekDayString(monday),
        mondayBillingHours: monday.getLong('billHours'),
        tuesday: this.createMasterweekDayString(tuesday),
        tuesdayBillingHours: tuesday.getLong('billHours'),
        wednesday: this.createMasterweekDayString(wednesday),
        wednesdayBillingHours: wednesday.getLong('billHours'),
        thursday: this.createMasterweekDayString(thursday),
        thursdayBillingHours: thursday.getLong('billHours'),
        friday: this.createMasterweekDayString(friday),
        fridayBillingHours: friday.getLong('billHours'),
        saturday: this.createMasterweekDayString(saturday),
        saturdayBillingHours: saturday.getLong('billHours'),
        sunday: this.createMasterweekDayString(sunday),
        sundayBillingHours: sunday.getLong('billHours'),
        totalShifts:
          (monday.getLong('billHours') ? 1 : 0)
          + (tuesday.getLong('billHours') ? 1 : 0)
          + (wednesday.getLong('billHours') ? 1 : 0)
          + (thursday.getLong('billHours') ? 1 : 0)
          + (friday.getLong('billHours') ? 1 : 0)
          + (saturday.getLong('billHours') ? 1 : 0)
          + (sunday.getLong('billHours') ? 1 : 0),
        totalHoursInMillis,
        totalHoursString: helpers.min2ClockHour(totalHoursInMillis / 1000 / 60),
      });
    });
    return dataSource;
  }

  createMasterweekDayString(masterweekDay) {
    return masterweekDay.getLong('billHours')
      ? `${masterweekDay.getString('fromTimeString')}-${masterweekDay.getString('toTimeString')} (${masterweekDay.getString('billHoursString')})`
      : '';
  }

  findCompanyName(masterweek) {
    const { services, companies } = this.props;

    const serviceId = [
      masterweek.getRemoteObject('monday').getLong('serviceId'),
      masterweek.getRemoteObject('tuesday').getLong('serviceId'),
      masterweek.getRemoteObject('wednesday').getLong('serviceId'),
      masterweek.getRemoteObject('thursday').getLong('serviceId'),
      masterweek.getRemoteObject('friday').getLong('serviceId'),
      masterweek.getRemoteObject('saturday').getLong('serviceId'),
      masterweek.getRemoteObject('sunday').getLong('serviceId'),
    ].find(el => !!el);

    const service = serviceId ? services.find(s => s.getId() === serviceId) : null;
    if (service) {
      const company = companies.find(c => c.id === service.getLong('companyId'));
      return company?.name;
    }

    return null;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'totalHoursString', 'totalShifts']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('mondayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('tuesdayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('wednesdayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('thursdayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('fridayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('saturdayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('sundayBillingHours'),
          TfootSummary.SUM_REDUCER_GENERATOR('totalHoursInMillis'),
          TfootSummary.SUM_REDUCER_GENERATOR('totalShifts'),
        ]}
        formatters={[
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => helpers.min2ClockHour(n / 1000 / 60),
          n => n,
        ]}
      />
    );
  }

  renderContent() {
    const { masterweeks, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="blue_hoki"
        fontSize="12px"
        size="small"
        title={title}
        icon="file-text"
        columns={this.createColumns()}
        dataSource={this.createDataSource(masterweeks)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableMasterweeks.propTypes = propTypes;
TableMasterweeks.defaultProps = defaultProps;

export default TableMasterweeks;
